import { Decimal } from "decimal.js";

// 符号优先级
const operatorPriority = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
};
// 辅助函数，用于计算两个操作数之间和一个运算符的结果，并将结果入结果栈
function calculate(
  operator: string,
  operand1: Decimal,
  operand2: Decimal,
): Decimal {
  switch (operator) {
    case "+":
      return operand2.plus(operand1);
    case "-":
      return operand2.minus(operand1);
    case "*":
      return operand2.times(operand1);
    case "/":
      return operand2.dividedBy(operand1);
    default:
      throw new Error(`Unknown operator: ${operator}`);
  }
}

/**
 * 计算给定表达式的值
 * @param expression 表达式字符串，
 * 请确保表达式格式正确
 * 如 1 + 2 * (3 + 4) / 5 - 6
 * 注意：表达式中只允许包含数字、+-*\/()和空格，不允许包含其他字符
 * 不允许出现连续两个运算符，如++ -- +-*\/
 * @param precision 保留小数位数，默认为空，表示不做处理
 * @returns
 */
export function calculateExpression(
  expression: string,
  precision?: number,
): string {
  // 预处理表达式，将一元负号转换为 (0 - number) 的形式
  let processedExpression = expression.replace(/\s+/g, "");

  // 处理开头的负号
  if (processedExpression.startsWith("-")) {
    processedExpression = "0" + processedExpression;
  }

  // 处理括号后、运算符后的负号
  processedExpression = processedExpression.replace(
    /([+\-*/()])\s*-\s*(\d+\.?\d*)/g,
    "$1(0-$2)",
  );

  // 将表达式解析为运算符数组和操作数数组
  const operators = processedExpression.match(/[-+*/()]|\d+\.?\d*/g) || [];
  const operands = operators.map((op) =>
    /^\d/.test(op) ? new Decimal(op) : op,
  );
  // 运算符栈和结果栈
  const operatorStack: string[] = [];
  const resultStack: (Decimal | string)[] = [];

  for (let i = 0; i < operators.length; i++) {
    const operator = operators[i];
    const operand = operands[i];

    // 遇到操作数，入结果栈
    if (operand instanceof Decimal) {
      resultStack.push(operand);
    } else if (operator === "(") {
      // 遇到左括号，直接入运算符栈
      operatorStack.push(operator);
    } else if (operator === ")") {
      // 遇到右括号
      // 1. 依次弹出运算符栈的运算符，并压入结果栈，直到遇到左括号为止
      // 2. 弹出左括号
      // 3. 依次弹出结果栈的运算数，并压入运算符栈，直到遇到右括号为止
      while (
        operatorStack.length &&
        operatorStack[operatorStack.length - 1] !== "("
      ) {
        const op = operatorStack.pop() as string;
        resultStack.push(
          calculate(
            op,
            resultStack.pop() as Decimal,
            resultStack.pop() as Decimal,
          ),
        );
      }
      operatorStack.pop();
    } else {
      // 当前运算符的优先级低于等于运算符栈顶优先级时，弹出栈顶运算符并计算
      while (
        operatorStack.length > 0 &&
        operatorPriority[operator as keyof typeof operatorPriority] <=
          operatorPriority[
            operatorStack[
              operatorStack.length - 1
            ] as keyof typeof operatorPriority
          ]
      ) {
        const topOperator = operatorStack.pop() as string;
        resultStack.push(
          calculate(
            topOperator,
            resultStack.pop() as Decimal,
            resultStack.pop() as Decimal,
          ),
        );
      }
      operatorStack.push(operator);
    }
  }
  // 将剩余的运算符依次计算
  while (operatorStack.length > 0) {
    const topOperator = operatorStack.pop() as string;
    resultStack.push(
      calculate(
        topOperator,
        resultStack.pop() as Decimal,
        resultStack.pop() as Decimal,
      ),
    );
  }

  // 最终结果即为结果栈中的唯一元素
  return typeof precision === "number"
    ? (resultStack[0] as Decimal).toFixed(precision)
    : resultStack[0].toString();
}
